/**
 * Narrative content for the About page. Authored copy lives here so it can be
 * edited without touching layout.
 */

export interface ValueEntry {
  name: string;
  description: string;
}

export interface LeaderEntry {
  name: string;
  title: string;
  initials: string;
}

export interface AwardEntry {
  year: string;
  title: string;
}

export interface StatEntry {
  figure: string;
  label: string;
}

export const aboutHero = {
  eyebrow: 'About Velnoris',
  headline: 'A Trusted Executive Recruitment Firm, 21 Years in the Making',
  supporting:
    'What began as a boutique advisory in London is now a global executive search and recruitment firm — a trusted partner for boards, employers, and career-driven professionals worldwide.',
};

export const aboutStory = {
  eyebrow: 'Our Story',
  heading: 'Built on relationships, proven by results',
  body: 'Velnoris Executive Network was founded on the conviction that executive search should be a strategic partnership, not a transaction. For over two decades we have advised Fortune 500 boards, high-growth startups, private equity portfolios, and government agencies through their most consequential leadership decisions.',
  closing:
    'Today, our consultants operate across 32 markets, combining local intelligence with a single, uncompromising global standard of service.',
};

export const aboutPillars = [
  {
    label: 'Our Mission',
    body: 'To connect exceptional talent with extraordinary organizations, unlocking growth for both.',
  },
  {
    label: 'Our Vision',
    body: 'A world where every organization is led by people who elevate the people around them.',
  },
  {
    label: 'Global Reach',
    body: 'Offices and partners spanning six continents, 32 markets, and every major industry.',
  },
];

export const aboutStats: StatEntry[] = [
  { figure: '32+', label: 'Countries served' },
  { figure: '4,800+', label: 'Executive placements' },
  { figure: '96%', label: 'Retention at 24 months' },
  { figure: '21yrs', label: 'Advisory heritage' },
];

export const corporateValues: ValueEntry[] = [
  { name: 'Integrity', description: 'We do the right thing, especially when no one is watching.' },
  { name: 'Excellence', description: 'We hold ourselves to the standard of the leaders we place.' },
  { name: 'Partnership', description: 'Your success is the only measure of ours.' },
  { name: 'Curiosity', description: 'We stay ahead by never assuming we know enough.' },
  { name: 'Inclusion', description: 'Diverse leadership builds more resilient organizations.' },
  { name: 'Discretion', description: 'Trust is our currency; confidentiality is absolute.' },
];

export const leadershipTeam: LeaderEntry[] = [
  { name: 'Adaeze Balogun', title: 'Global Managing Partner', initials: 'AB' },
  { name: 'Julian Reinhardt', title: 'Head of Executive Search', initials: 'JR' },
  { name: 'Meera Krishnan', title: 'Partner, Talent Advisory', initials: 'MK' },
  { name: 'Kenji Tanaka', title: 'Partner, Leadership Consulting', initials: 'KT' },
];

export const awards: AwardEntry[] = [
  { year: '2024', title: 'Global Executive Search Firm of the Year' },
  { year: '2023', title: 'Best Diversity Hiring Practice — Talent Awards' },
  { year: '2022', title: 'Top 10 Leadership Advisory Consultancies' },
  { year: '2021', title: 'Innovation in Talent Intelligence Award' },
];

export const aboutCommitments = [
  {
    heading: 'Diversity & Inclusion',
    body: 'Over half of our placed executives come from underrepresented groups — because balanced leadership performs better.',
  },
  {
    heading: 'Community Impact',
    body: 'Our pro-bono practice supports non-profits and social enterprises with leadership advisory each year.',
  },
];

export const aboutCta = {
  heading: 'Let’s build the leadership your ambition demands.',
  body: 'Partner with advisors who understand that the right leader changes everything.',
};
